import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate.js';
import { ButtonSecondary, ButtonTransparent } from '../../Utils/Buttons/button-component.js';
import SystemDataService from '../../Services/SystemDataService.js';
import { getServicesData } from '../../Public/services-data';
import { getServicePageUI } from '../../Public/service-page-data';
import Image from '../../Utils/Image/image-component';
import SkeletonBuilder from '../../Utils/Skeleton/skeleton-builder-component';
import NavLink from '../../Utils/NavLink/navlink-component';
import MediaComponent from '../../Utils/Media/media-component.js';

function ServicesSection({
    limit = null,
    title = null,
    isHeadline = false,
    showMore = false,
    showMoreText = null,
    sectionId = null,
    landingPage = false,
    filterCategoryId = null,   // when set, only shows services whose categoryId matches
}) {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);

    const handleRedirect = useHandleRedirect();
    const handleScrollNavigate = useScrollOrNavigate();

    const systemData = useSelector((s) => s.systemData || {});
    const { language = 'he' } = systemData;
    const dir = systemData.dir || (language === 'he' ? 'rtl' : 'ltr');
    const isRtl = dir === 'rtl' || language === 'he';

    const phoneData = SystemDataService.byLabel(systemData.links, 'Phone');

    const uiContent = getServicePageUI(language).servicesListSection;
    const defaultViewAllText = uiContent?.viewAll || (isRtl ? 'לכל השירותים' : 'View All Services');

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);

            try {
                const servicesData = await getServicesData(language);
                const all = servicesData || [];
                setServices(
                    filterCategoryId
                        ? all.filter((s) => s.categoryId === filterCategoryId)
                        : all
                );
            } catch (err) {
                console.error('Failed to fetch services:', err);
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [language, filterCategoryId]);

    const slicedServices = useMemo(() => {
        if (limit && Number.isInteger(limit)) return services.slice(0, limit);
        return services;
    }, [services, limit]);

    const servicesWithLayout = useMemo(() => {
        return (slicedServices || []).map((svc, idx) => {
            const renderTitle = svc.card?.title || svc.cardTitle || svc.headline || svc.name;
            const renderDescription = svc.card?.description || svc.cardDescription || svc.subHeadline || svc.description;
            const renderImage = svc.card?.image || svc.cardImage || svc.image || svc.media;
            const renderBtnText =uiContent.learnMore;
            const renderLink = svc.actionHref || (svc.slug ? `/services/${svc.slug}` : '#');

            // Even rows → white bg / Odd rows → navy bg
            const isDark = idx % 2 !== 0;

            return {
                ...svc,
                id: svc.id || idx,
                renderTitle,
                renderDescription,
                renderImage,
                renderBtnText,
                renderLink,
                phoneData,
                isReversed: isRtl ? (idx % 2 !== 0) : (idx % 2 !== 0),
                isDark,
                idx,
            };
        });
    }, [slicedServices, phoneData, uiContent, isRtl]);

    const displayServices = loading
        ? Array.from({ length: limit || 3 }).map((_, idx) => ({
              id: `placeholder-${idx}`,
              isDark: idx % 2 !== 0,
              isReversed: idx % 2 !== 0,
              idx,
          }))
        : servicesWithLayout;

    const handleButtonClick = (e, link) => {
        if (landingPage) handleScrollNavigate(e, 'contact-form', link);
        else handleRedirect(e, link);
    };

    if (!loading && displayServices.length === 0) return null;

    return (
        <section id={sectionId || undefined} dir={dir} className="flex flex-col w-full font-primary">

            {isHeadline && (
                <div className="relative flex w-full justify-center bg-primary-950 py-16 lg:py-28 px-6 border-b border-white/10 overflow-hidden">
                    {/* Background image from first service */}
                    {services[0]?.hero?.imageDesktop || services[0]?.card?.image || services[0]?.image ? (
                        <div className="absolute inset-0 z-0">
                            <img
                                src={services[0]?.hero?.imageDesktop || services[0]?.card?.image || services[0]?.image}
                                alt=""
                                className="w-full h-full object-cover opacity-20"
                            />
                            <div className="absolute inset-0 bg-primary-950/60" />
                        </div>
                    ) : null}
                    <div className={`relative z-10 flex max-w-7xl w-full flex-col gap-4 ${isRtl ? 'text-right items-end' : 'text-left items-start'}`}>
                        {uiContent.label && (
                            <span className="text-secondary-400 font-bold uppercase text-xs md:text-sm font-primary">
                                {uiContent.label}
                            </span>
                        )}

                        <h2 className="font-secondary uppercase text-5xl md:text-7xl font-extrabold text-white leading-[0.95]">
                            {uiContent.headline}
                        </h2>

                        {uiContent.subHeadline && (
                            <p className="text-xl md:text-2xl text-white/60 max-w-3xl leading-relaxed font-bold">
                                {uiContent.subHeadline}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {title && (
                <div className="w-full bg-primary-950 py-10 px-6">
                    <div className="max-w-7xl mx-auto">
                        <h2 className={`font-secondary uppercase text-4xl md:text-5xl font-extrabold text-white ${isRtl ? 'text-right' : 'text-left'}`}>
                            {title}
                        </h2>
                    </div>
                </div>
            )}

            {displayServices.map((svc) => {
                const lightRow = !svc.isDark;
                const bg = lightRow ? 'bg-white' : 'bg-primary-600';
                const headingColor = lightRow ? 'text-primary-500 hover:text-secondary-500' : 'text-white hover:text-secondary-500';
                const bodyColor = lightRow ? 'text-primary-800/65' : 'text-white/65';
                const numColor = lightRow ? 'text-primary-500/10' : 'text-white/10';
                const tagColor = lightRow ? 'text-secondary-500' : 'text-secondary-500';
                const borderColor = lightRow ? 'border-primary-100' : 'border-white/10';

                return (
                    <div key={svc.id} className={`${bg} w-full`}>
                        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
                            <div
                                className={`w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center ${
                                    svc.isReversed
                                        ? 'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1'
                                        : ''
                                }`}
                            >
                                {/* Image */}
                                <NavLink
                                    href={svc.renderLink}
                                    title={svc.renderTitle}
                                    showUnderline={false}
                                    className="w-full relative group !p-0 !opacity-100 block"
                                >
                                    <div className="w-full rounded-3xl overflow-hidden relative z-10">
                                        {svc.renderImage ? (
                                            <MediaComponent
                                                quality={80}
                                                width={700}
                                                src={svc.renderImage}
                                                alt={svc.renderTitle}
                                                objectFit="contain"
                                                useSekeleton={false}
                                                wrapperClassName="w-full"
                                                className="!h-auto transition-transform duration-500 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="w-full aspect-[4/3] bg-primary-800/30 animate-pulse rounded-3xl" />
                                        )}
                                    </div>
                                </NavLink>

                                {/* Text */}
                                <div className={`flex flex-col ${isRtl ? 'items-end text-right' : 'items-start text-left'}`}>
                                    {loading ? (
                                        <div className="flex flex-col gap-6 w-full animate-pulse">
                                            <SkeletonBuilder type="LargeText" count={1} />
                                            <SkeletonBuilder type="SmallText" count={3} />
                                            <SkeletonBuilder type="Button" />
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-6">
                                          
                                            <NavLink
                                                href={svc.renderLink}
                                                title={svc.renderTitle}
                                                showUnderline={false}
                                                className={`font-secondary font-extrabold text-4xl md:text-5xl leading-[1.05] transition-colors !p-0 !opacity-100 ${headingColor}`}
                                            >
                                                {svc.renderTitle}
                                            </NavLink>

                                            {svc.renderDescription && (
                                                <p className={`text-lg md:text-xl font-semibold leading-relaxed ${bodyColor}`}>
                                                    {svc.renderDescription}
                                                </p>
                                            )}

                                            <div className="mt-2 w-full max-w-md sm:w-auto">
                                                {lightRow ? (
                                                    <ButtonSecondary
                                                        size="lg"
                                                        href={!landingPage ? svc.renderLink : undefined}
                                                        onClick={landingPage ? (e) => handleButtonClick(e, svc.renderLink) : undefined}
                                                        className="!rounded-full font-bold"
                                                    >
                                                        {svc.renderBtnText}
                                                    </ButtonSecondary>
                                                ) : (
                                                    <ButtonTransparent
                                                        size="lg"
                                                        href={!landingPage ? svc.renderLink : undefined}
                                                        onClick={landingPage ? (e) => handleButtonClick(e, svc.renderLink) : undefined}
                                                        className="!rounded-full font-bold !border-white !text-white hover:!bg-white hover:!text-primary-500"
                                                    >
                                                        {svc.renderBtnText}
                                                    </ButtonTransparent>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}

            {showMore && (
                <div className="w-full bg-white py-14 px-6">
                    <div className="max-w-lg mx-auto">
                        <ButtonSecondary
                            size="lg"
                            fullWidth
                            href="/services"
                            className="!rounded-full font-bold shadow-2xl"
                        >
                            {showMoreText || defaultViewAllText}
                        </ButtonSecondary>
                    </div>
                </div>
            )}
        </section>
    );
}

export default ServicesSection;
